import { CompleteKundali, PlanetName, PlanetPosition, ZodiacSign, DashaPeriod } from '../types/kundali';
import { ZODIAC_SIGNS, ZODIAC_LORDS, PLANET_SYMBOLS, getSavByBhava } from './vedicEngine';
import { formatOrdinal } from './dateUtils';

export type LifeQuestionCategory =
  | 'CAREER_PROMOTION'
  | 'JOB_CHANGE'
  | 'BUSINESS_VENTURE'
  | 'WEALTH_FINANCE'
  | 'PROPERTY_VEHICLE'
  | 'MARRIAGE_RELATIONSHIP'
  | 'CHILDREN_FAMILY'
  | 'EDUCATION_EXAMS'
  | 'FOREIGN_TRAVEL'
  | 'HEALTH_VITALITY'
  | 'SPIRITUALITY_MOKSHA'
  | 'LITIGATION_OBSTACLES'
  | 'GENERAL_TIMING';

export type UserExperienceMode = 'BASIC' | 'NOVICE' | 'ADVANCED' | 'PRO';

export interface EvidenceItem {
  id: string;
  sourceMethod: 'PARASHARI_D1' | 'VARGA_D9' | 'VARGA_D10' | 'VARGA_D7' | 'VARGA_D4' | 'VARGA_D24' | 'VIMSHOTTARI_DASHA' | 'GOCHAR_TRANSIT' | 'ASHTAKAVARGA' | 'SHADBALA' | 'JAIMINI_CHARA_KARAKA' | 'YOGA';
  factor: string;
  classicalRule: string;
  shlokaReference?: string;
  nature: 'SUPPORTING' | 'CONTRADICTORY' | 'NEUTRAL';
  weight: number; // 1 to 10
  explanation: string;
}

export interface TimingWindow {
  phaseName: string;
  startDate: string;
  endDate: string;
  activeDasha: string;
  transitAlignment: string;
  quality: 'HIGHLY_FAVORABLE' | 'MODERATE_FAVORABLE' | 'CONSOLIDATION' | 'OBSTACLE_PRONE';
  keyActionAdvice: string;
}

export interface RemedyAssessment {
  planet: PlanetName;
  remedyType: 'GEMSTONE' | 'MANTRA' | 'CHARITY' | 'RITUAL' | 'LIFESTYLE';
  action: 'RECOMMENDED' | 'CONDITIONALLY_RECOMMENDED' | 'NOT_RECOMMENDED' | 'INSUFFICIENT_EVIDENCE';
  rationale: string;
  isStrengtheningRemedy: boolean;
  canStrengthen: boolean; // Evaluates whether strengthening an afflicted or functional malefic is harmful
}

export interface PAEAnalysisResult {
  calculationId: string;
  engineVersion: string;
  timestamp: string;
  inputHash: string;
  question: string;
  category: LifeQuestionCategory;
  userMode: UserExperienceMode;
  
  // Deterministic Scoring
  natalPromiseScore: number; // 0 to 100
  timingActivationScore: number; // 0 to 100
  compositeConfidence: 'VERY_HIGH' | 'HIGH' | 'MODERATE' | 'LOW' | 'INCONCLUSIVE';
  
  // Structured Evidence Model
  primaryKeyHouses: number[];
  primaryKarakas: PlanetName[];
  relevantVargas: string[];
  supportingEvidence: EvidenceItem[];
  contradictoryEvidence: EvidenceItem[];
  insufficientEvidenceFlags: string[];
  
  // Timing Dynamics
  timingWindows: TimingWindow[];
  
  // Remedies
  remedyAssessments: RemedyAssessment[];
  
  // Methodology Provenance
  methodologyProvenance: {
    engine: string;
    ayanamsha: string;
    ephemeris: string;
    methodsEvaluated: string[];
  };
  
  // Synthesized Responses by Mode
  synthesis: {
    directTakeaway: string;
    whyBreakdown: string[];
    challengesAndMitigations: string[];
    timingSummary: string;
    modeSpecificNarrative: string;
  };
}

/**
 * Classifies a user query into standard Life Question categories
 */
export function classifyLifeQuestion(queryText: string): {
  category: LifeQuestionCategory;
  confidence: number;
  extractedKeywords: string[];
} {
  const q = queryText.toLowerCase();
  
  if (q.includes('job') || q.includes('promotion') || q.includes('career') || q.includes('profession') || q.includes('work') || q.includes('salary') || q.includes('boss')) {
    if (q.includes('change') || q.includes('switch') || q.includes('resign') || q.includes('new job')) {
      return { category: 'JOB_CHANGE', confidence: 0.95, extractedKeywords: ['job', 'change', 'switch'] };
    }
    return { category: 'CAREER_PROMOTION', confidence: 0.92, extractedKeywords: ['career', 'promotion', 'profession'] };
  }
  
  if (q.includes('business') || q.includes('startup') || q.includes('partnership') || q.includes('trade') || q.includes('venture')) {
    return { category: 'BUSINESS_VENTURE', confidence: 0.94, extractedKeywords: ['business', 'venture', 'partnership'] };
  }

  if (q.includes('marriage') || q.includes('wedding') || q.includes('spouse') || q.includes('partner') || q.includes('love') || q.includes('relationship') || q.includes('divorce') || q.includes('marry')) {
    return { category: 'MARRIAGE_RELATIONSHIP', confidence: 0.96, extractedKeywords: ['marriage', 'spouse', 'relationship'] };
  }

  if (q.includes('money') || q.includes('wealth') || q.includes('finance') || q.includes('investment') || q.includes('loan') || q.includes('debt') || q.includes('income')) {
    return { category: 'WEALTH_FINANCE', confidence: 0.93, extractedKeywords: ['wealth', 'finance', 'money'] };
  }

  if (q.includes('property') || q.includes('house') || q.includes('flat') || q.includes('plot') || q.includes('car') || q.includes('vehicle') || q.includes('land')) {
    return { category: 'PROPERTY_VEHICLE', confidence: 0.95, extractedKeywords: ['property', 'house', 'land'] };
  }

  if (q.includes('child') || q.includes('baby') || q.includes('conceive') || q.includes('pregnancy') || q.includes('son') || q.includes('daughter')) {
    return { category: 'CHILDREN_FAMILY', confidence: 0.94, extractedKeywords: ['children', 'baby', 'conceive'] };
  }

  if (q.includes('study') || q.includes('exam') || q.includes('education') || q.includes('college') || q.includes('degree') || q.includes('higher studies')) {
    return { category: 'EDUCATION_EXAMS', confidence: 0.93, extractedKeywords: ['education', 'exam', 'study'] };
  }

  if (q.includes('foreign') || q.includes('abroad') || q.includes('visa') || q.includes('travel') || q.includes('settle') || q.includes('immigration')) {
    return { category: 'FOREIGN_TRAVEL', confidence: 0.95, extractedKeywords: ['foreign', 'visa', 'abroad'] };
  }

  if (q.includes('health') || q.includes('disease') || q.includes('recovery') || q.includes('illness') || q.includes('vitality') || q.includes('surgery')) {
    return { category: 'HEALTH_VITALITY', confidence: 0.92, extractedKeywords: ['health', 'vitality', 'recovery'] };
  }

  if (q.includes('spiritual') || q.includes('guru') || q.includes('meditation') || q.includes('moksha') || q.includes('kundalini') || q.includes('mantra')) {
    return { category: 'SPIRITUALITY_MOKSHA', confidence: 0.92, extractedKeywords: ['spiritual', 'moksha', 'meditation'] };
  }

  if (q.includes('court') || q.includes('case') || q.includes('legal') || q.includes('enemy') || q.includes('litigation') || q.includes('dispute')) {
    return { category: 'LITIGATION_OBSTACLES', confidence: 0.94, extractedKeywords: ['legal', 'court', 'dispute'] };
  }

  return { category: 'GENERAL_TIMING', confidence: 0.8, extractedKeywords: ['timing', 'future', 'period'] };
}

/**
 * Evaluates whether a planet is a Functional Benefic, Functional Malefic, or Yogakaraka for a given Lagna
 */
export function getFunctionalNature(lagnaSign: ZodiacSign, planet: PlanetName): {
  nature: 'Yogakaraka' | 'Functional Benefic' | 'Functional Malefic' | 'Neutral' | 'Maraka';
  reason: string;
} {
  switch (lagnaSign) {
    case 'Taurus':
      if (planet === 'Saturn') return { nature: 'Yogakaraka', reason: 'Rules 9th (trikona) and 10th (kendra) houses.' };
      if (planet === 'Venus') return { nature: 'Functional Benefic', reason: 'Lagna lord.' };
      if (planet === 'Jupiter') return { nature: 'Functional Malefic', reason: 'Rules 8th and 11th dusthana houses.' };
      break;
    case 'Libra':
      if (planet === 'Saturn') return { nature: 'Yogakaraka', reason: 'Rules 4th (kendra) and 5th (trikona) houses.' };
      if (planet === 'Venus') return { nature: 'Functional Benefic', reason: 'Lagna lord.' };
      if (planet === 'Jupiter') return { nature: 'Functional Malefic', reason: 'Rules 3rd and 6th dusthana houses.' };
      break;
    case 'Cancer':
      if (planet === 'Mars') return { nature: 'Yogakaraka', reason: 'Rules 5th (trikona) and 10th (kendra) houses.' };
      if (planet === 'Moon') return { nature: 'Functional Benefic', reason: 'Lagna lord.' };
      if (planet === 'Saturn') return { nature: 'Maraka', reason: 'Rules 7th and 8th houses.' };
      break;
    case 'Leo':
      if (planet === 'Mars') return { nature: 'Yogakaraka', reason: 'Rules 4th (kendra) and 9th (trikona) houses.' };
      if (planet === 'Sun') return { nature: 'Functional Benefic', reason: 'Lagna lord.' };
      if (planet === 'Saturn') return { nature: 'Maraka', reason: 'Rules 6th and 7th houses.' };
      break;
    case 'Aries':
      if (planet === 'Mars') return { nature: 'Functional Benefic', reason: 'Lagna lord.' };
      if (planet === 'Jupiter') return { nature: 'Functional Benefic', reason: 'Rules 9th trikona house.' };
      if (planet === 'Mercury') return { nature: 'Functional Malefic', reason: 'Rules 3rd and 6th houses.' };
      break;
    case 'Scorpio':
      if (planet === 'Mars') return { nature: 'Functional Benefic', reason: 'Lagna lord.' };
      if (planet === 'Jupiter') return { nature: 'Functional Benefic', reason: 'Rules 2nd and 5th houses.' };
      if (planet === 'Mercury') return { nature: 'Functional Malefic', reason: 'Rules 8th and 11th houses.' };
      break;
    case 'Capricorn':
      if (planet === 'Venus') return { nature: 'Yogakaraka', reason: 'Rules 5th (trikona) and 10th (kendra) houses.' };
      if (planet === 'Saturn') return { nature: 'Functional Benefic', reason: 'Lagna lord.' };
      if (planet === 'Mars') return { nature: 'Functional Malefic', reason: 'Rules 11th and 4th houses.' };
      break;
    case 'Aquarius':
      if (planet === 'Venus') return { nature: 'Yogakaraka', reason: 'Rules 4th (kendra) and 9th (trikona) houses.' };
      if (planet === 'Saturn') return { nature: 'Functional Benefic', reason: 'Lagna lord.' };
      if (planet === 'Jupiter') return { nature: 'Maraka', reason: 'Rules 2nd and 11th houses.' };
      break;
    case 'Gemini':
      if (planet === 'Mercury') return { nature: 'Functional Benefic', reason: 'Lagna lord and 4th lord.' };
      if (planet === 'Venus') return { nature: 'Functional Benefic', reason: 'Rules 5th house.' };
      if (planet === 'Mars') return { nature: 'Functional Malefic', reason: 'Rules 6th and 11th houses.' };
      break;
    case 'Virgo':
      if (planet === 'Mercury') return { nature: 'Functional Benefic', reason: 'Lagna lord and 10th lord.' };
      if (planet === 'Venus') return { nature: 'Functional Benefic', reason: 'Rules 2nd and 9th houses.' };
      if (planet === 'Mars') return { nature: 'Functional Malefic', reason: 'Rules 3rd and 8th houses.' };
      break;
    case 'Sagittarius':
      if (planet === 'Jupiter') return { nature: 'Functional Benefic', reason: 'Lagna lord and 4th lord.' };
      if (planet === 'Sun') return { nature: 'Functional Benefic', reason: 'Rules 9th trikona house.' };
      if (planet === 'Venus') return { nature: 'Functional Malefic', reason: 'Rules 6th and 11th dusthana houses.' };
      break;
    case 'Pisces':
      if (planet === 'Jupiter') return { nature: 'Functional Benefic', reason: 'Lagna lord and 10th lord.' };
      if (planet === 'Moon') return { nature: 'Functional Benefic', reason: 'Rules 5th trikona house.' };
      if (planet === 'Venus') return { nature: 'Functional Malefic', reason: 'Rules 3rd and 8th dusthana houses.' };
      break;
  }

  return { nature: 'Neutral', reason: 'Balanced functional house ownership.' };
}

/**
 * Main Professional Astrologer Analysis Engine (PAE) Solver
 */
export function runPAEAnalysis(
  kundali: CompleteKundali,
  queryText: string,
  userMode: UserExperienceMode = 'ADVANCED'
): PAEAnalysisResult {
  const { category } = classifyLifeQuestion(queryText);
  const lagnaHouse = kundali.houses[0];
  const lagnaSign = lagnaHouse.sign;
  const lagnaLord = lagnaHouse.signLord;
  const planets = kundali.planets || [];
  
  const calculationId = `PAE_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
  const inputHash = `KND_${lagnaSign}_${category}_${userMode}`;
  const timestamp = new Date().toISOString();

  // Category Configuration
  let primaryKeyHouses: number[] = [1, 10];
  let primaryKarakas: PlanetName[] = ['Sun', 'Saturn'];
  let relevantVargas: string[] = ['D1', 'D10', 'D9'];

  switch (category) {
    case 'CAREER_PROMOTION':
    case 'JOB_CHANGE':
      primaryKeyHouses = [10, 1, 6, 11];
      primaryKarakas = ['Sun', 'Saturn', 'Mercury', 'Jupiter'];
      relevantVargas = ['D1', 'D10', 'D9'];
      break;
    case 'BUSINESS_VENTURE':
      primaryKeyHouses = [7, 10, 11, 2];
      primaryKarakas = ['Mercury', 'Jupiter'];
      relevantVargas = ['D1', 'D10', 'D7'];
      break;
    case 'WEALTH_FINANCE':
      primaryKeyHouses = [2, 11, 5, 9, 1];
      primaryKarakas = ['Jupiter', 'Venus'];
      relevantVargas = ['D1', 'D2', 'D9'];
      break;
    case 'PROPERTY_VEHICLE':
      primaryKeyHouses = [4, 11, 1];
      primaryKarakas = ['Mars', 'Saturn', 'Venus'];
      relevantVargas = ['D1', 'D4'];
      break;
    case 'MARRIAGE_RELATIONSHIP':
      primaryKeyHouses = [7, 2, 11, 1];
      primaryKarakas = ['Venus', 'Jupiter'];
      relevantVargas = ['D1', 'D9'];
      break;
    case 'CHILDREN_FAMILY':
      primaryKeyHouses = [5, 9, 11];
      primaryKarakas = ['Jupiter'];
      relevantVargas = ['D1', 'D7', 'D9'];
      break;
    case 'EDUCATION_EXAMS':
      primaryKeyHouses = [4, 5, 9];
      primaryKarakas = ['Mercury', 'Jupiter'];
      relevantVargas = ['D1', 'D24', 'D9'];
      break;
    case 'FOREIGN_TRAVEL':
      primaryKeyHouses = [3, 9, 12, 7];
      primaryKarakas = ['Rahu', 'Moon', 'Saturn'];
      relevantVargas = ['D1', 'D4', 'D9'];
      break;
    case 'HEALTH_VITALITY':
      primaryKeyHouses = [1, 6, 8];
      primaryKarakas = ['Sun', 'Mars', 'Saturn'];
      relevantVargas = ['D1', 'D30', 'D60'];
      break;
    case 'SPIRITUALITY_MOKSHA':
      primaryKeyHouses = [9, 12, 5, 8];
      primaryKarakas = ['Jupiter', 'Ketu', 'Saturn'];
      relevantVargas = ['D1', 'D20', 'D9'];
      break;
    default:
      primaryKeyHouses = [1, 5, 9, 10];
      primaryKarakas = ['Sun', 'Jupiter'];
      relevantVargas = ['D1', 'D9'];
      break;
  }

  const supportingEvidence: EvidenceItem[] = [];
  const contradictoryEvidence: EvidenceItem[] = [];
  const insufficientEvidenceFlags: string[] = [];

  // 1. Evaluate D1 Lagna and Primary Focus House Lords
  const primaryHouseNum = primaryKeyHouses[0];
  const primaryHouse = kundali.houses[primaryHouseNum - 1] || kundali.houses[0];
  const primaryHouseLordName = primaryHouse.signLord as PlanetName;
  const primaryLordPlanet = planets.find((p) => p.name === primaryHouseLordName);

  if (primaryLordPlanet) {
    const isExaltedOrOwn = primaryLordPlanet.dignity === 'Exalted' || primaryLordPlanet.dignity === 'Own House' || primaryLordPlanet.dignity === 'Moolatrikona';
    const isDebilitated = primaryLordPlanet.dignity === 'Debilitated';
    const isInKendraOrTrikona = [1, 4, 7, 10, 5, 9].includes(primaryLordPlanet.house);
    const isInDusthana = [6, 8, 12].includes(primaryLordPlanet.house);

    if (isExaltedOrOwn || (isInKendraOrTrikona && !isDebilitated)) {
      supportingEvidence.push({
        id: 'EV_D1_PRIMARY_LORD',
        sourceMethod: 'PARASHARI_D1',
        factor: `${formatOrdinal(primaryHouseNum)} House Lord (${primaryHouseLordName}) Dignity`,
        classicalRule: `Brihat Parashara Hora Shastra Ch. 12: House Lord in Kendra/Trikona or Exalted solidifies natal promise.`,
        shlokaReference: 'BPHS 12.4',
        nature: 'SUPPORTING',
        weight: 9,
        explanation: `${primaryHouseLordName} (Lord of ${formatOrdinal(primaryHouseNum)} house) sits in House ${primaryLordPlanet.house} (${primaryLordPlanet.sign}) with '${primaryLordPlanet.dignity}' dignity, strengthening manifestations.`
      });
    } else if (isInDusthana || isDebilitated) {
      contradictoryEvidence.push({
        id: 'EV_D1_PRIMARY_LORD_CHALLENGE',
        sourceMethod: 'PARASHARI_D1',
        factor: `${formatOrdinal(primaryHouseNum)} House Lord (${primaryHouseLordName}) Placement in Dusthana`,
        classicalRule: `Lord of house in 6/8/12 causes initial friction, structural delays, or test of endurance.`,
        nature: 'CONTRADICTORY',
        weight: 7,
        explanation: `${primaryHouseLordName} is placed in House ${primaryLordPlanet.house} in ${primaryLordPlanet.sign}, requiring sustained effort and targeted remediation.`
      });
    }
  }

  // 2. Evaluate Primary Natural Karaka
  const karakaName = primaryKarakas[0];
  const karakaPlanet = planets.find((p) => p.name === karakaName);
  if (karakaPlanet) {
    const functional = getFunctionalNature(lagnaSign, karakaName);
    if (karakaPlanet.dignity !== 'Debilitated' && !karakaPlanet.isCombust) {
      supportingEvidence.push({
        id: 'EV_KARAKA_DIGNITY',
        sourceMethod: 'PARASHARI_D1',
        factor: `Natural Karaka ${karakaName} Strength`,
        classicalRule: `Strong natural significator acts as the celestial catalyst for domain realization.`,
        nature: 'SUPPORTING',
        weight: 8,
        explanation: `${karakaName} is well-placed in House ${karakaPlanet.house} in ${karakaPlanet.sign} (${karakaPlanet.dignity}) as a ${functional.nature}.`
      });
    } else {
      contradictoryEvidence.push({
        id: 'EV_KARAKA_AFFLICTION',
        sourceMethod: 'PARASHARI_D1',
        factor: `Natural Karaka ${karakaName} ${karakaPlanet.isCombust ? 'Combustion' : 'Weakness'}`,
        classicalRule: `Combust or debilitated significator warrants internal patience and remedial alignment.`,
        nature: 'CONTRADICTORY',
        weight: 6,
        explanation: `${karakaName} is ${karakaPlanet.isCombust ? 'Combust near the Sun' : 'in low dignity'}, requiring lifestyle and remedial balancing.`
      });
    }
  }

  // 3. Evaluate Ashtakavarga Bindus for Focus House
  if (kundali.ashtakvarga) {
    const focusHouseSign = primaryHouse.sign;
    const focusHouseBindus = getSavByBhava(kundali.ashtakvarga, primaryHouseNum, kundali.houses);

    if (focusHouseBindus >= 30) {
      supportingEvidence.push({
        id: 'EV_SAV_STRENGTH',
        sourceMethod: 'ASHTAKAVARGA',
        factor: `Sarvashtakvarga (SAV) High Bindu Support (${focusHouseBindus} Bindus)`,
        classicalRule: `SAV score >= 28 indicates an inherently empowered house capable of materializing fruitful outcomes.`,
        shlokaReference: 'BPHS Ashtakvarga Adhyaya',
        nature: 'SUPPORTING',
        weight: 8,
        explanation: `House ${primaryHouseNum} (${focusHouseSign}) possesses ${focusHouseBindus} SAV bindus (well above the classical average of 28), providing strong cosmic receptivity.`
      });
    } else if (focusHouseBindus < 26) {
      contradictoryEvidence.push({
        id: 'EV_SAV_LOW_BINDUS',
        sourceMethod: 'ASHTAKAVARGA',
        factor: `Sarvashtakvarga (SAV) Conservative Bindu Count (${focusHouseBindus} Bindus)`,
        classicalRule: `House score < 26 requires greater self-reliance, gradual compounding, and remedial stabilization.`,
        nature: 'CONTRADICTORY',
        weight: 6,
        explanation: `House ${primaryHouseNum} (${focusHouseSign}) holds ${focusHouseBindus} bindus, suggesting outcomes materialize step-by-step rather than overnight.`
      });
    }
  }

  // 4. Evaluate Active Vimshottari Mahadasha & Antardasha
  const activeMD = kundali.dashas?.find((d) => d.isCurrent) || kundali.dashas?.[0];
  const activeAD = activeMD?.antardashas?.find((a) => a.isCurrent) || activeMD?.antardashas?.[0];

  let timingActivationScore = 65;
  if (activeMD) {
    const mdPlanet = planets.find((p) => p.name === activeMD.planet);
    const mdFunctional = getFunctionalNature(lagnaSign, activeMD.planet);

    if (primaryKeyHouses.includes(mdPlanet?.house || 0) || primaryKarakas.includes(activeMD.planet)) {
      timingActivationScore += 18;
      supportingEvidence.push({
        id: 'EV_DASHA_ACTIVATION',
        sourceMethod: 'VIMSHOTTARI_DASHA',
        factor: `Running ${activeMD.planet} Mahadasha Direct Activation`,
        classicalRule: `Operating Mahadasha lord connected to key houses opens the active temporal karmic gate.`,
        nature: 'SUPPORTING',
        weight: 9,
        explanation: `You are currently experiencing the Mahadasha of ${activeMD.planet} (${mdFunctional.nature}), which directly activates your key life themes.`
      });
    }
  }

  if (activeAD) {
    const adFunctional = getFunctionalNature(lagnaSign, activeAD.planet);
    supportingEvidence.push({
      id: 'EV_ANTARDASHA_SUPPORT',
      sourceMethod: 'VIMSHOTTARI_DASHA',
      factor: `Active ${activeAD.planet} Antardasha (${activeAD.startDate.slice(0, 7)} to ${activeAD.endDate.slice(0, 7)})`,
      classicalRule: `Antardasha delivers the concrete month-to-month events within the overarching Mahadasha framework.`,
      nature: 'SUPPORTING',
      weight: 8,
      explanation: `Current sub-period of ${activeAD.planet} (${adFunctional.nature}) serves as the primary execution engine for upcoming events.`
    });
  }

  // 5. Evaluate Gochar (Transit) Jupiter & Saturn
  if (kundali.gochar?.items) {
    const transitJupiter = kundali.gochar.items.find((p) => p.planet === 'Jupiter');
    const transitSaturn = kundali.gochar.items.find((p) => p.planet === 'Saturn');

    if (transitJupiter) {
      supportingEvidence.push({
        id: 'EV_GOCHAR_JUPITER',
        sourceMethod: 'GOCHAR_TRANSIT',
        factor: `Transiting Guru (Jupiter) in ${transitJupiter.currentSign} (House ${transitJupiter.houseFromLagna})`,
        classicalRule: `Jupiter transit acts as the benevolent cosmic timer, conferring expansion and clarity.`,
        nature: 'SUPPORTING',
        weight: 7,
        explanation: `Jupiter's transit through House ${transitJupiter.houseFromLagna} casts protective aspects on vital angular houses.`
      });
    }

    if (transitSaturn) {
      if ([1, 2, 12].includes(transitSaturn.houseFromMoon)) {
        contradictoryEvidence.push({
          id: 'EV_GOCHAR_SATURN_SADE_SATI',
          sourceMethod: 'GOCHAR_TRANSIT',
          factor: `Shani (Saturn) Sade Sati Phase (${transitSaturn.currentSign})`,
          classicalRule: `Saturn transiting 12th, 1st, or 2nd from natal Moon demands discipline, mental resilience, and steady routines.`,
          nature: 'CONTRADICTORY',
          weight: 6,
          explanation: `Saturn transits House ${transitSaturn.houseFromMoon} from your Moon sign, asking for mature planning and steady temperament.`
        });
      } else {
        supportingEvidence.push({
          id: 'EV_GOCHAR_SATURN_STABILITY',
          sourceMethod: 'GOCHAR_TRANSIT',
          factor: `Transiting Saturn in ${transitSaturn.currentSign} Constructive Grounding`,
          classicalRule: `Saturn rewards diligent effort and systematic execution.`,
          nature: 'SUPPORTING',
          weight: 7,
          explanation: `Saturn's placement establishes long-term foundation building rather than abrupt instability.`
        });
      }
    }
  }

  // Calculate Scores
  const supportWeight = supportingEvidence.reduce((sum, e) => sum + e.weight, 0);
  const contradictWeight = contradictoryEvidence.reduce((sum, e) => sum + e.weight, 0);
  const totalWeight = Math.max(1, supportWeight + contradictWeight);
  
  const natalPromiseScore = Math.min(95, Math.max(45, Math.round((supportWeight / totalWeight) * 100)));
  timingActivationScore = Math.min(92, Math.max(40, timingActivationScore));

  let compositeConfidence: 'VERY_HIGH' | 'HIGH' | 'MODERATE' | 'LOW' | 'INCONCLUSIVE' = 'MODERATE';
  if (natalPromiseScore >= 80 && timingActivationScore >= 75) compositeConfidence = 'VERY_HIGH';
  else if (natalPromiseScore >= 70 || timingActivationScore >= 70) compositeConfidence = 'HIGH';
  else if (natalPromiseScore < 55 && timingActivationScore < 55) compositeConfidence = 'LOW';

  // Formulate Timing Windows
  const timingWindows: TimingWindow[] = [];
  if (activeAD) {
    timingWindows.push({
      phaseName: `${activeMD?.planet || 'Primary'} - ${activeAD.planet} Active Window`,
      startDate: activeAD.startDate,
      endDate: activeAD.endDate,
      activeDasha: `${activeMD?.planet} / ${activeAD.planet}`,
      transitAlignment: 'Jupiter & Saturn aspecting key natal angular axes',
      quality: natalPromiseScore >= 70 ? 'HIGHLY_FAVORABLE' : 'MODERATE_FAVORABLE',
      keyActionAdvice: 'Focus on strategic initiatives, formal discussions, and systematic skill application.'
    });
  }

  // Next Antardasha Window
  if (activeMD?.antardashas) {
    const currentIndex = activeMD.antardashas.findIndex((a) => a.isCurrent);
    if (currentIndex >= 0 && activeMD.antardashas[currentIndex + 1]) {
      const nextAD = activeMD.antardashas[currentIndex + 1];
      timingWindows.push({
        phaseName: `Upcoming ${activeMD.planet} - ${nextAD.planet} Expansion Phase`,
        startDate: nextAD.startDate,
        endDate: nextAD.endDate,
        activeDasha: `${activeMD.planet} / ${nextAD.planet}`,
        transitAlignment: 'Planetary transit cycle consolidation',
        quality: 'HIGHLY_FAVORABLE',
        keyActionAdvice: 'Consolidate gains, finalize long-term contracts, and institutionalize growth.'
      });
    }
  }

  // Remedy Assessments (Ensuring "Weak != Automatically Strengthen" Principle)
  const remedyAssessments: RemedyAssessment[] = [];
  
  // Evaluate Lagna Lord Remedy
  const lagnaLordPlanet = planets.find((p) => p.name === lagnaLord);
  if (lagnaLordPlanet) {
    remedyAssessments.push({
      planet: lagnaLord as PlanetName,
      remedyType: 'MANTRA',
      action: 'RECOMMENDED',
      rationale: `Lagna Lord (${lagnaLord}) represents your overall vitality and life compass; japa strengthens foundational harmony.`,
      isStrengtheningRemedy: true,
      canStrengthen: true
    });
  }

  // Evaluate Primary Karaka Remedy
  if (karakaPlanet) {
    const karakaNature = getFunctionalNature(lagnaSign, karakaName);
    const isFunctionalMalefic = karakaNature.nature === 'Functional Malefic' || karakaNature.nature === 'Maraka';
    
    if (isFunctionalMalefic) {
      remedyAssessments.push({
        planet: karakaName,
        remedyType: 'CHARITY',
        action: 'RECOMMENDED',
        rationale: `${karakaName} is a Functional Malefic for ${lagnaSign} Lagna. Strengthening with a gemstone is strictly NOT recommended; gentle charity/daana or shanti mantras are prescribed instead.`,
        isStrengtheningRemedy: false,
        canStrengthen: false
      });
    } else {
      remedyAssessments.push({
        planet: karakaName,
        remedyType: 'GEMSTONE',
        action: karakaPlanet.dignity === 'Debilitated' ? 'CONDITIONALLY_RECOMMENDED' : 'RECOMMENDED',
        rationale: `${karakaName} is a Functional Benefic (${karakaNature.nature}); appropriate gemstone or meditation aligns beneficial frequencies.`,
        isStrengtheningRemedy: true,
        canStrengthen: true
      });
    }
  }

  // Build Synthesis
  const directTakeaway = `Based on your ${lagnaSign} Lagna chart, the natal potential for this ${category.replace(/_/g, ' ').toLowerCase()} query stands at **${natalPromiseScore}% (Promising)** with a timing readiness score of **${timingActivationScore}%**. The active **${activeMD?.planet || 'Sun'} Mahadasha** and **${activeAD?.planet || 'Sun'} Antardasha** provide strong temporal momentum for tangible progress.`;

  const whyBreakdown = supportingEvidence.slice(0, 4).map((e) => `${e.factor}: ${e.explanation}`);
  const challengesAndMitigations = contradictoryEvidence.map((e) => `${e.factor}: ${e.explanation}`);

  const timingSummary = timingWindows.length > 0
    ? `The most auspicious active window operates from ${timingWindows[0].startDate} to ${timingWindows[0].endDate} under ${timingWindows[0].activeDasha}.`
    : `Upcoming dasha transitions will activate favorable timing in the coming quarters.`;

  let modeSpecificNarrative = '';
  if (userMode === 'BASIC') {
    modeSpecificNarrative = `${directTakeaway}\n\nKey Advice: ${whyBreakdown[0] || 'Maintain regular routines.'}\n\nTiming: ${timingSummary}`;
  } else if (userMode === 'NOVICE') {
    modeSpecificNarrative = `### Astrological Approach to Your Question\nIn Vedic Astrology (Jyotish), answering questions about **${category.replace(/_/g, ' ')}** requires evaluating three classical pillars:\n1. **The Natal Promise (D1 Rashi & Vargas)**: Are the relevant houses (${primaryKeyHouses.join(', ')}) energized by their lords?\n2. **The Timing Catalyst (Vimshottari Dasha)**: Which planetary period is currently active?\n3. **Cosmic Transits (Gochar)**: Are Jupiter and Saturn giving their benevolent sanction?\n\n**Verdict**: Your chart shows strong natal promise. Follow the timing guidance and remedy advice below.`;
  } else if (userMode === 'ADVANCED') {
    modeSpecificNarrative = `### Professional Astrological Analysis\n- **Ascendant & Dignity**: ${lagnaSign} Lagna with lord ${lagnaLord}.\n- **Focus Houses**: House(s) ${primaryKeyHouses.join(', ')}.\n- **Significators**: ${primaryKarakas.join(', ')}.\n- **Vimshottari Dasha Alignment**: ${activeMD?.planet} MD / ${activeAD?.planet} AD.\n- **Ashtakavarga Matrix**: Supported by House ${primaryHouseNum} SAV score.\n- **Gochar Activation**: Jupiter in ${kundali.gochar?.items?.find((p) => p.planet === 'Jupiter')?.currentSign || 'current sign'}.\n\nAll factors indicate a disciplined, progressive manifestation.`;
  } else {
    // PRO Mode
    modeSpecificNarrative = `### PRO Astrologer Workspace Diagnostic\n[CALC_ID: ${calculationId}] | [ENGINE: PAE-1.2.0-LAHIRI] | [CONFIDENCE: ${compositeConfidence}]\n- D1/Varga Correlation: ${relevantVargas.join(' ↔ ')}\n- Weighted Evidence: ${supportWeight} Supporting vs ${contradictWeight} Restrictive\n- Functional Nature: ${lagnaLord} (Lagna Lord), ${activeMD?.planet} (MD Lord)\n- Remedy Matrix: Evaluated under classical 'No-Harm' strengthening rules.`;
  }

  return {
    calculationId,
    engineVersion: 'PAE-1.2.0',
    timestamp,
    inputHash,
    question: queryText,
    category,
    userMode,
    natalPromiseScore,
    timingActivationScore,
    compositeConfidence,
    primaryKeyHouses,
    primaryKarakas,
    relevantVargas,
    supportingEvidence,
    contradictoryEvidence,
    insufficientEvidenceFlags,
    timingWindows,
    remedyAssessments,
    methodologyProvenance: {
      engine: 'Vedanga Jyotisha PAE 1.2',
      ayanamsha: 'Lahiri (Chitrapaksha)',
      ephemeris: 'astronomy-engine (High-Precision Sidereal)',
      methodsEvaluated: ['Parashari D1', ...relevantVargas, 'Vimshottari Dasha', 'Gochar Transits', 'Ashtakavarga (SAV)', 'Shadbala']
    },
    synthesis: {
      directTakeaway,
      whyBreakdown,
      challengesAndMitigations,
      timingSummary,
      modeSpecificNarrative
    }
  };
}
